import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { EndpointHint, HelpIcon, LoadFailure, RefreshButton, Skeleton } from '../../core/index.js'
import { useUnitNames } from '../../facilities/index.js'
import { fetchStaffMessage, useFacilityMessages } from '../hooks.js'
import type { StaffMessage, StaffMessageWithReach } from '../types.js'
import { StaffMessageComposer } from './StaffMessageComposer.js'
import { describeReach, describeRecipients, formatSentAt } from './staffMessageText.js'

/** How many sent messages the card lists. */
const SHOWN = 30

/**
 * The Message workers sub-tab of the facility's Tools tab (SMS plan section
 * 5): the composer to the roster, then the messages sent, newest first, each
 * opening to how it reached its recipients.
 */
export function MessageWorkersCard({ facilityId }: { facilityId: string }) {
  const list = useFacilityMessages(facilityId, SHOWN)
  const units = useUnitNames(facilityId)
  return (
    <div className="bg-white border border-marine/15 rounded p-5 flex flex-col gap-5">
      <div>
        <div className="text-sm font-black text-ink mb-2">
          Message workers
          <HelpIcon topic="messages.message-workers" className="ml-1" />
          <EndpointHint className="ml-2">POST /facilities/{'{facilityId}'}/messages</EndpointHint>
        </div>
        <StaffMessageComposer facilityId={facilityId} onSent={() => void list.refetch()} />
      </div>
      <div>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <div className="flex items-baseline gap-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            Sent
            <EndpointHint>GET /facilities/{'{facilityId}'}/messages</EndpointHint>
          </div>
          <RefreshButton size="sm" onRefresh={() => list.refetch()} refreshing={list.isFetching} />
        </div>
        {list.isPending ? (
          <Skeleton />
        ) : list.isError ? (
          <LoadFailure size="xs" message="The messages sent could not be loaded." onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : (
          <SentMessageList messages={list.data.items} units={units} reachOf={(messageId) => fetchStaffMessage(facilityId, messageId)} />
        )}
      </div>
    </div>
  )
}

/** The messages sent, newest first; a row opens to its reach, read on request through `reachOf` (the session's call, or the admin's). Shared with the admin's dialog. */
export function SentMessageList({ messages, units, reachOf, empty = 'Nothing sent yet.' }: { messages: readonly StaffMessage[]; /** The facility's units by id, so an audience names a unit rather than its id. */ units?: ReadonlyMap<string, string>; reachOf: (messageId: string) => Promise<StaffMessageWithReach>; empty?: string }) {
  const [open, setOpen] = useState<string | null>(null)
  if (messages.length === 0) {
    return <p className="text-xs text-ink-mid">{empty}</p>
  }
  return (
    <ul className="divide-y divide-marine/10 rounded border border-marine/15 bg-white">
      {messages.map((message) => (
        <li key={message.messageId} className="px-3 py-2 text-[11px]">
          <button type="button" className="flex w-full flex-wrap items-baseline justify-between gap-x-3 text-left hover:opacity-90" onClick={() => setOpen(open === message.messageId ? null : message.messageId)} aria-expanded={open === message.messageId}>
            <span className="min-w-0 flex-1 text-ink">“{message.text}”</span>
            <span className="text-ink-mid">
              {describeRecipients(message, units)} · {formatSentAt(message.sentAt)}
              {message.sentBy.name ? ` · ${message.sentBy.name}` : ''}
              {message.urgent ? <span className="ml-1.5 rounded bg-coral/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-coral">Urgent</span> : null}
            </span>
          </button>
          {open === message.messageId && <ReachLine messageId={message.messageId} reachOf={reachOf} />}
        </li>
      ))}
    </ul>
  )
}

function ReachLine({ messageId, reachOf }: { messageId: string; reachOf: (messageId: string) => Promise<StaffMessageWithReach> }) {
  const reach = useQuery({ queryKey: ['staff-message-reach', messageId], queryFn: () => reachOf(messageId) })
  if (reach.isPending) {
    return <p className="mt-1 text-ink-mid">Counting…</p>
  }
  if (reach.isError) {
    return <p className="mt-1 text-coral">The reach could not be counted.</p>
  }
  return <p className="mt-1 text-ink-mid">{describeReach(reach.data.reach)}</p>
}
